import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { v4 as uuidv4 } from 'uuid';
import { useRequestController } from '../../../Home/hooks/useRequestController';
import { transformMessages } from '../../../ChatPage/utils';
import {
  SKILL_DETAIL_CACHE_STALE_TIME,
  SKILL_LINKAGE_SESSION_CACHE_KEY_PREFIX,
} from '../../constants/skillDetail';
import type { ISkillLinkage } from '../../types/skillDetail';
import type { GetSnapshotDataType } from '../../../ChatShare/types/snapshotDataTypes';

/**
 * 获取联动案例对话数据
 * 直接读结构化字段；cacheKey 由中英文 ID 拼成，切语种命中缓存
 * @param linkage - 联动案例项
 * @param isEN - 是否英文环境
 * @param enabled - 是否启用请求
 */
const useSkillLinkageSessionData = (
  linkage: ISkillLinkage,
  isEN: boolean,
  enabled = true,
) => {
  const userInfo = useSelector((state: any) => state.home.userInfo, shallowEqual);
  const cnId = linkage.case_display_session_id?.trim() || '';
  const enId = linkage.case_display_session_id_en?.trim() || '';
  const displayId = isEN ? enId : cnId;
  const sessionType = linkage.case_session_type;
  const cacheKey = (cnId || enId)
    ? `${SKILL_LINKAGE_SESSION_CACHE_KEY_PREFIX}${cnId}_${enId}_${displayId}`
    : undefined;

  const query = useMemo(() => {
    const baseQuery = {
      trace_id: uuidv4(),
      user_id: userInfo?.userid,
      page: 0,
      count: 999,
    };

    if (sessionType === 'message') {
      return { ...baseQuery, message_id: displayId };
    }

    return { ...baseQuery, session_id: displayId };
  }, [sessionType, displayId, userInfo?.userid]);

  const messagesRequest = useRequestController({
    method: 'POST',
    url: '/api/agent_com/get_all_message_in_session',
    cacheKey,
    refreshDeps: query,
    ready: enabled && Boolean(displayId)
      && (sessionType === 'session' || sessionType === 'message'),
    cacheTime: SKILL_DETAIL_CACHE_STALE_TIME,
    staleTime: SKILL_DETAIL_CACHE_STALE_TIME,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
  });

  const data = useMemo(() => {
    const res = messagesRequest.data as GetSnapshotDataType | undefined;
    if (!res || res?.res_code !== 0) {
      return null;
    }
    return { ...res, messages: transformMessages(res.messages) };
  }, [messagesRequest.data]);

  return {
    data,
    loading: messagesRequest.loading,
  };
};

export default useSkillLinkageSessionData;
